// 録音エンジンの公開契約 (state / params / return / 中断エラー)。
//
// L3 (deriveViewStatus / recording-status) と legacy consumer は、エンジンの実装
// (useRecordingEngine.ts) ではなく本 module に依存する。streaming transport の
// 物理分割 (apps/legacy の生 field 直読みが消えた後) を行うときも、この契約は動かない。
import type { SessionType } from '@/shared/types/yakurekiCore'
import type { RecordingPreset } from './streaming/recordingPreset'

/**
 * engine.cancel() の結果。deletedEventIds = back が削除したフリー録音の event id
 * (cancel 応答の deleted_event_ids)。通知失敗 / session 無しのときは空。
 */
export type CancelResult = {
  deletedEventIds: string[]
}

/**
 * 録音エンジンのライフサイクル状態
 *
 * - idle: 未開始
 * - starting: session 作成 + mic/AudioContext + MediaRecorder 並列起動中
 * - recording: worklet chunk を送信中 + MediaRecorder が並行録音中
 * - fallback: mic + MediaRecorder だけで録音している状態。劣化 (任意の chunk/finalize
 *   エラーで streaming から切替. 録音は継続) と、意図した batch 録音 (start route='batch'.
 *   log/207) の両方がここに入る。意味の区別は isIntendedBatch が持つ
 * - pausing: 一時停止処理中 (= セグメント blob を POST /pause/ で確定させている)
 * - paused: 一時停止確定済 (log/185 セグメント確定方式)。engine はマイク・blob・queue の
 *   いずれも保持せず、識別子 (sessionId / segmentIndex) だけを持つ。正本はサーバ側
 *   (SS.status=PAUSED + StreamingSegment)。resume() で次セグメント録音へ、stop() で
 *   blob 無し finalize (= staged セグメントのみで SOAP 生成) へ遷移する。
 * - resuming: 再開処理中 (= POST /resume/ + ローカルパイプライン再構築)
 * - finalizing: stop 要求を受けて finalize multipart (audio 同梱) を実行中
 * - completed: 正常終了 (streaming なら finalize 成功、 fallback なら onFallbackCommit 完了)
 * - failed: エラー終了
 */
export type RecordingEngineState =
  | 'idle'
  | 'starting'
  | 'recording'
  | 'fallback'
  | 'pausing'
  | 'paused'
  | 'resuming'
  | 'finalizing'
  | 'completed'
  | 'failed'

/**
 * 中断種別 (= unrecoverable).
 *
 * - track-mute: MediaStreamTrack.muted=true (= iOS 着信等で OS が mic を奪った)
 * - audio-context-suspended: AudioContext.state が suspended/interrupted (= OS audio session 中断)
 *
 * visibilitychange (= desktop tab hidden) は recoverable として `switchToFallback` で扱うので
 * 本型には含めない.
 */
export type InterruptionReason = 'track-mute' | 'audio-context-suspended'

/**
 * `state === 'failed'` の error 種別判別用. caller / banner は `instanceof` で
 * RecordingInterruptedError と finalize 失敗系 (= 通常 `Error`) を振り分ける.
 */
export class RecordingInterruptedError extends Error {
  readonly reason: InterruptionReason

  constructor(reason: InterruptionReason) {
    super(`recording interrupted: ${reason}`)
    this.name = 'RecordingInterruptedError'
    this.reason = reason
  }
}

export type RecordingStartParams = {
  yakurekiIds: string[]
  pharmacistId?: string
  memoItems?: string[]
  /**
   * 対象患者の家族 (同伴/代理) 有無を event ごとに指定する。
   * yakureki-back の SOAP 生成 prompt 振り分け規則テーブルに反映される
   * (log/145/work-log/01)。省略時は全 event False (家族行なし)。
   */
  hasCompanionOrSubstituteMap?: Record<string, boolean>
  /**
   * フリー録音 mode. true なら yakurekiIds=[] を渡し、 back が空 Event 1 件を
   * auto-create する。 hook return の `recordingEventIds` に id が surfaced される.
   */
  isFreeRecording?: boolean
  /** フリー録音時の label override. 省略時 back が 'Meeting YYYY-MM-DD HH:MM' で自動採番. */
  label?: string
  /**
   * 来局/在宅の別。フリー録音時のみ有効 (auto-create される Event に載る)。
   * 患者紐付け録音は既存 Event の属性なので一覧側の PATCH session-type で変える。
   */
  sessionType?: SessionType
  /**
   * 録音時プリセット (auto / deep)。ユーザーが選んだ生成 intent を
   * back に送る (model tier の解決・過去履歴の注入判定は back の責務)。省略時 back default。
   * route='batch' では運べない (legacy batch upload に受け口が無い) ため caller 側で
   * 選択 UI ごと出さない。
   */
  preset?: RecordingPreset
  /**
   * 転写経路 (log/207 PR-FRONT ①)。省略時 'streaming'。
   * 'batch' は streaming session を張らず mic + MediaRecorder だけで録音し、
   * 停止時に fallback commit 経路 (= onFallbackCommit = legacy batch upload) へ流す。
   * 劣化 fallback と同じ機構を使うが、isIntendedBatch=true で意味を区別する。
   */
  route?: 'streaming' | 'batch'
  /**
   * 収音制約の上書き (log/207 F)。noiseSuppression はユーザーが設定画面で変更でき、
   * autoGainControl は admin 専用の実験用 override。
   * custom_setting の capture_noise_suppression / capture_auto_gain_control 由来で、
   * key 無し・null は現行の既定値 (noiseSuppression=true / autoGainControl=false) を使う。
   * resume (一時停止からの再開) にも同じ値が引き継がれる。
   */
  captureOverrides?: {
    noiseSuppression?: boolean | null
    autoGainControl?: boolean | null
  }
  /** MediaRecorder の bitrate。省略時はブラウザ既定 (audio/mp4 で約 128 kbps)。 */
  audioBitsPerSecond?: number
}

/**
 * Hook の caller が指定する optional な責務委譲点.
 *
 * `onFallbackCommit` を渡すと、 fallback 経路で stop された時の blob を
 * hook 内で `await commit(blob)` する自走モードになり、 caller は完了時に
 * `state='completed'` を観測するだけで良くなる. 失敗時は pendingBlob 経由で
 * retry/DL 救済 UX に倒れる (= `RecordingFailedAlert` 経路再利用).
 *
 * `onFallbackCommit` を渡さない場合は従来通り `fallbackBlob` を surface する
 * (apps/legacy の useRecordAudio が useEffect で拾う流儀との backward-compat).
 */
export type RecordingEngineArgs = {
  onFallbackCommit?: (blob: Blob) => Promise<void>
}

/**
 * 一時停止セッションの復元情報 (log/185 サーバ復元).
 * 正本は event 詳細 serializer の `paused_streaming_session` (= サーバ側 PAUSED SS +
 * StreamingSegment から導出)。sessionStorage には依存しない。
 */
export type PausedSessionInfo = {
  sessionId: string
  eventIds: string[]
  nextSegmentIndex: number
  /** 確定済みセグメントの duration 合計 (経過タイマー表示の復元に使う) */
  stagedDurationS: number
}

export type RecordingEngineReturn = {
  state: RecordingEngineState
  sessionId: string | null
  recordingSessionId: string | null
  error: Error | null
  /**
   * fallback 経路で stop された時に surface される blob。
   * caller は本 blob を legacy batch 経路 (`POST /api/yakureki/` または
   * `POST /api/recording-sessions/upload_audio/`) に送る。
   * 正常 streaming 経路では null のまま (backend 側で finalize multipart 同梱した
   * audio を persist_stream_audio で RS.audio に紐付け済)。
   *
   * 注意: hook 呼出時に `onFallbackCommit` を渡したならば hook が自走で blob を
   * 投げ込むため、 caller は本 field を観測する必要なし (常に null). 渡されない
   * 場合のみ apps/legacy 流の useEffect 拾いで使う backward-compat field.
   */
  fallbackBlob: Blob | null
  /**
   * `state === 'failed'` で local 保持中の録音 blob.
   * RecordingFailedAlert (= MeetingBlock 配線) が `[再試行]` / `[音声をダウンロード]`
   * の救済 UX で参照する. 服薬指導録音は二度撮りできない医療 record 制約のため、
   * finalize / start 通信失敗で SS が壊れても client 側で blob を握って復旧導線を出す.
   * `retry()` 成功 / `reset()` / `cancel()` で null に戻る.
   */
  pendingBlob: Blob | null
  /**
   * `pendingBlob !== null` の derived flag. UI は本 flag で button 表示を gate する.
   */
  hasPendingBlob: boolean
  /**
   * navigator.onLine + 'online'/'offline' event を反映した live なオフライン signal.
   * オフライン中の finalize は TanStack Query (networkMode='online') が mutation を pause し
   * `state` が 'finalizing' のまま固まるため、 UI は本 flag で「通信待ち」 を判別して
   * 救済バナー (= [音声をダウンロード] 逃げ道) を出す.
   */
  isOffline: boolean
  /**
   * 停止後の文字起こし・SOAP 生成が低速経路 (legacy batch) で走っているか.
   * - client 起点の MediaRecorder fallback は commit 成功時に true (= 常に legacy batch).
   * - backend 起点は finalize response の `transcription_mode: 'batch_fallback'`
   *   (= SS=FAILED 由来の GS-3 / finalize 時の chunk 欠落検知 chunk_audio_gap) で true.
   * UI は SOAP 生成完了までの間「低速モードで文字起こし中です」notice に使う.
   * start() / cancel() で false に戻る. reset() では維持する: reset は「次の録音に
   * 備える engine cleanup」で、 IndexPage は engine='completed' 直後に terminal cleanup
   * として自動で呼ぶが、 その時点で server 側の低速 batch 生成はまだ進行中のため.
   */
  isSlowTranscription: boolean
  /**
   * 現録音 (またはその直後の生成) が「意図した batch 経路」か (log/207 PR-FRONT ①).
   * engine の 'fallback' 状態は mic + MediaRecorder だけで録音している機構の名前で、
   * 「streaming が死んだ (= 劣化)」という意味は本 flag が false の場合にだけ持つ.
   * UI は connectionStatus (黄バナー) の抑止と低速 notice の文言分岐に使う.
   * ライフサイクルは isSlowTranscription と同じ (start/cancel で false・reset では維持).
   */
  isIntendedBatch: boolean
  /**
   * 録音中の生 MediaStream. 録音ボタンの脈動 ring (useAudioPulse) など、stream を直接消費する UI 用に露出する.
   * idle / starting / 録音終了後は null. teardown とともに null に戻る.
   */
  stream: MediaStream | null
  /**
   * start 後に back から受領した event id 配列. フリー録音時は auto-create された 1 件、
   * 紐付け録音 / まとめて録音時は start で渡した値と同じ. idle / reset で空配列に戻る.
   * Phase A の `/recording/new` から `/soap/:id` への navigate に使う.
   */
  recordingEventIds: string[]
  /**
   * 録音開始. 返り値は「caller が失敗として通知すべきか」:
   * - true = 開始成功、 または cancel() 割込による静かな撤退 (= ユーザー自身の操作)
   * - false = 開始失敗 (マイク許可拒否 / session 作成失敗など)。 旧救済 blob を保持
   *   していた場合は engine が start 前の failed 状態に丸ごと巻き戻っているため、
   *   caller は toast 等でこの start の失敗だけを通知する.
   */
  start: (params: RecordingStartParams) => Promise<boolean>
  stop: () => Promise<void>
  /**
   * 一時停止 (log/185): 現時点までを 1 セグメントとして back に確定させ (SOAP は蹴らない)、
   * マイク・blob・エンジンを全解放して 'paused' に遷移する。streaming 'recording' 中のみ有効。
   * - 'paused': 成功 (cancel 割込による静かな撤退も含む)
   * - 'failed': POST 失敗 (retry 救済に倒れる) → caller は toast
   * - 'noop': guard 拒否 (= 既に paused/pausing 等. 二重押下)。caller は何もしない
   *   (検証 B7: 失敗と区別して誤 error toast を出さない)
   */
  pause: () => Promise<'paused' | 'failed' | 'noop'>
  /**
   * 一時停止からの再開: POST /resume/ でサーバの PAUSED→ACTIVE を戻し、ローカル録音
   * パイプラインを再構築して次セグメントの 'recording' に入る。'paused' 中のみ有効。
   * - 'resumed': 成功 (cancel 割込による静かな撤退も含む)
   * - 'failed': 一時的な失敗。'paused' に留まり error を surface (再度 resume 可能)
   * - 'expired': サーバ側 session が消滅。袋小路を避けるため idle に戻した (検証 F6)
   * - 'noop': guard 拒否 (= 既に resuming/recording 等. 二重押下)。caller は何もしない (検証 B7)
   */
  resume: () => Promise<'resumed' | 'failed' | 'expired' | 'noop'>
  /**
   * サーバ由来の paused 復元情報 (event 詳細の paused_streaming_session) を engine に
   * 復元する。'idle' 中のみ有効。復元後は resume() / stop() / cancel() が通常どおり使える。
   * 正本がサーバ側にあるため、 患者切替・reload・ブラウザ再起動・別端末を全て跨げる。
   */
  adoptPausedSession: (info: PausedSessionInfo) => void
  /**
   * 確定済みセグメントの duration 合計 (秒)。paused 中の経過タイマー表示と、 resume 後の
   * タイマー継続起点に使う。pause 成功 / adopt で更新、 start / reset で 0 に戻る。
   */
  stagedDurationS: number
  /**
   * ACTIVE 中の真のキャンセル. backend に POST /cancel/ を投げ、
   * SS=CANCELLED("user_cancelled") + 内部 state を idle に戻す。
   * 録音中断ボタン押下など、ユーザーが意図的に録音をやめる場合に呼ぶ。
   *
   * 内部 state の idle 復帰と mic 解放は**同期**で先に済ませ、backend への通知だけを
   * await 可能にする (= 返る promise は backend が応答した時点で解決する)。
   * 通知の失敗は握り潰す (UI は破棄完了に倒す・deletedEventIds は空)。session が無ければ即 resolve。
   * cache をどう扱うか (楽観更新 → await → 一覧から外す → invalidate) は呼び出し元
   * (useRecordingStatus.discardRecording) の責務。
   */
  cancel: () => Promise<CancelResult>
  /**
   * 完了後の cleanup 専用 reset. backend には通知せず、内部 state のみ idle に戻す。
   * `state === 'completed'` / `'failed'` 後に caller hook が cleanup として呼ぶ用途。
   * cancel() を流用すると finalize 済 SS が backend で誤って書き換わる問題があるため、
   * cleanup と意図的キャンセルを明確に分離する。
   */
  reset: () => void
  /**
   * `state === 'failed'` から保持 blob で送信を再投入する. 宛先は失敗時の文脈で決まる:
   * pause 再送 (同 segment_index) / salvage・streaming finalize (sessionId 宛) /
   * fallback commit (batch・劣化とも sessionId 不要. onFallbackCommit へ).
   * 成功すれば `state='completed'` (pause 再送は 'paused') + pendingBlob クリア、
   * 失敗すれば `state='failed'` のまま. pendingBlob が null の場合は no-op.
   */
  retry: () => Promise<void>
  /**
   * 保持 blob を local file に音声 DL する. ファイル名は `kakeru-recording-{ISO 日時}.{ext}`.
   * pendingBlob が null の場合は no-op.
   */
  downloadBlob: () => void
}
